import React, { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useErrorHandler } from "react-error-boundary";
import styled from "styled-components";
import { BookingState, PendingBooking } from "../../types";
import { updatePendingBookingRequest } from "../../requests/pending-booking.requests";
import { pendingBookingStore } from "../../stores/pending-booking.store";
import { SummaryCriteria } from "../summary-criteria/summary-criteria";
import { ConfirmationContactForm } from "./confirmation-contact-form/confirmation-contact-form";
import { ConfirmationPayment } from "./confirmation-payment/confirmation-payments";
import {
  GolContactInfoFormContext,
  GolContactInfoInterface,
} from "./confirmation-contact-form/gol-contact-form-interfaces";
import { ContinueButton } from "../continue-button/continue-button";
import { ConfirmationCommitPendingBooking } from "./confirmation-commit-pending-booking";
import { Summary } from "./summary/summary";
import { InformationText } from "../information-text/information-text";
import { useTranslation } from "react-i18next";
import { moduleSettings } from "../../stores/module-settings.store";
import ReCAPTCHA from "react-google-recaptcha";
import { verifyReCaptcha } from "../../requests/recaptcha.requests";

const PrepaymentInfoWrapper = styled.div`
  width: 100%;
  margin-top: 32px;
`;

export const ConfirmationLayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Anchor = styled.div`
  width: 1px;
  height: 1px;
`;

const ReCaptcha = styled(ReCAPTCHA)`
  margin-top: 24px;
`;

const Confirmation = () => {
  const { t } = useTranslation();
  const [bookingState] = useState(pendingBookingStore.getBookingState());
  const [errorMsg] = useState(useLocation().state as { [key: string]: string });
  const [prepaymentInfo, setPrepaymentInfo] = useState(
    pendingBookingStore.prepaymentInfo
  );

  const [showPptForm, setShowPptForm] = useState(
    !moduleSettings.hasSkippingGroupPpt
  );
  const [submitText, setSubmitButtonText] = useState("");
  const [validContactInfo, setValidContactInfo] = useState(false);
  const [validPptInfo, setValidPptInfo] = useState(false);
  const [checkContactInfoFormValidity, setCheckContactInfoFormValidity] =
    useState(false);
  const [checkContactPptInfoFormValidity, setCheckContactPptInfoFormValidity] =
    useState(false);
  const [canCommit, setCanCommit] = useState(false);
  const isRecaptchaEnabled = moduleSettings.isRecaptchaEnabled;
  const [captchaValue, setCaptchaValue] = useState(null);
  const throwError = useErrorHandler();

  const { i18n } = useTranslation();
  useEffect(() => {
    setPrepaymentInfo(pendingBookingStore.prepaymentInfo);
  }, [i18n.language]);

  useEffect(() => {
    let buttonText_ = "button.reserve";
    if (prepaymentInfo || bookingState === BookingState.initialized) {
      buttonText_ = "button.ask_for_reservation";
    }
    if (bookingState === BookingState.waiting) {
      buttonText_ = "button.ask_for_waiting_list";
    }
    if (showPptForm && moduleSettings.hasSkippingGroupPpt) {
      buttonText_ = "button.confirm_payment";
    }

    setSubmitButtonText(buttonText_);
  }, [
    bookingState,
    prepaymentInfo,
    showPptForm,
    moduleSettings.hasSkippingGroupPpt,
  ]);

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    if (moduleSettings.hasSkippingGroupPpt && prepaymentInfo && showPptForm) {
      const anchor = document.querySelector("#anchor-stripe-info");
      if (anchor && !isInViewport(anchor)) {
        anchor?.scrollIntoView({ block: "end", inline: "nearest" });
      }
    }
  });

  useEffect(() => {
    if (!checkContactInfoFormValidity) setValidContactInfo(false);
    setCheckContactPptInfoFormValidity(false);
  }, [checkContactInfoFormValidity]);

  useEffect(() => {
    if (validPptInfo) {
      //!\disabled push stripeToken update
      //since stripeWebHook is being used
      /* const subscription = updatePendingBookingRequest({
         stripe_token: pendingBookingStore.stripeToken,
       }).subscribe((resp) => {
         pendingBookingStore.pendingBooking = {
           ...pendingBookingStore.pendingBooking,
           ...resp,
         };
         setCanCommit(true);
       }, throwError);
       return () => {
         subscription.unsubscribe();
       };*/
      verifyCanCommit();
    }
  }, [validPptInfo, throwError]);

  useEffect(() => {
    if (validContactInfo) {
      const subscription = updatePendingBookingRequest({
        contact_info: pendingBookingStore.pendingBookingContact,
      }).subscribe((resp: PendingBooking) => {
        pendingBookingStore.pendingBooking = {
          ...pendingBookingStore.pendingBooking,
          ...resp,
        };
        if (resp.prepayment) {
          if (pendingBookingStore.skip_prepayment_for_contact) {
            verifyCanCommit();
          } else {
            if (moduleSettings.hasSkippingGroupPpt) {
              if (!showPptForm) {
                setShowPptForm(true);
                setCheckContactInfoFormValidity(false);
              } else {
                setCheckContactPptInfoFormValidity(true);
              }
            } else {
              setCheckContactPptInfoFormValidity(true);
            }
          }
        } else {
          verifyCanCommit();
        }
      }, throwError);
      return () => {
        subscription.unsubscribe();
      };
    } else {
      setCheckContactInfoFormValidity(false);
    }
  }, [validContactInfo, throwError]);

  const checkFormValidity = () => {
    setCheckContactInfoFormValidity(true);
  };

  const verifyCanCommit = () => {
    if (isRecaptchaEnabled) {
      const subscription = verifyReCaptcha(captchaValue!).subscribe((resp) => {
        if (resp === 200) {
          pendingBookingStore.recaptchaValue = captchaValue!;
          setCanCommit(true);
        }
      }, throwError);
      return () => {
        subscription.unsubscribe();
      };
    } else {
      setCanCommit(true);
    }
  };

  const onChange = (value: React.SetStateAction<null>) => {
    setCaptchaValue(value);
  };

  const value: GolContactInfoInterface = {
    setValidContactInfo,
    setValidPptInfo,
    checkContactInfoFormValidity,
    checkContactPptInfoFormValidity,
    setCheckContactInfoFormValidity,
    setCheckContactPptInfoFormValidity,
    errorMsg,
  };

  const content = (
    <>
      <ConfirmationLayoutWrapper>
        <SummaryCriteria data-test="selection-criteria" />
        <Summary />

        <GolContactInfoFormContext.Provider value={value}>
          <ConfirmationContactForm
            clazz={checkContactInfoFormValidity ? "disabled" : ""}
          />
          {prepaymentInfo && showPptForm && (
            <PrepaymentInfoWrapper data-test="ppt-info-wrapper">
              <InformationText text={prepaymentInfo} />
              <InformationText
                text={t("instabook.prepayment.3d_secure_info")}
                status={"info"}
              />
            </PrepaymentInfoWrapper>
          )}
          {prepaymentInfo && showPptForm && (
            <ConfirmationPayment
              data-test="confirmation-payment"
              clazz={checkContactInfoFormValidity ? "disabled" : ""}
            />
          )}
          {isRecaptchaEnabled && (
            <ReCaptcha
              sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY}
              onChange={onChange}
              hl={i18n.language}
            />
          )}
          <ContinueButton
            buttonTextKey={submitText}
            onClick={checkFormValidity}
            loader={checkContactInfoFormValidity}
            disabled={
              checkContactInfoFormValidity ||
              (isRecaptchaEnabled && !captchaValue)
            }
          />
          <Anchor id="anchor-stripe-info" />
        </GolContactInfoFormContext.Provider>
      </ConfirmationLayoutWrapper>
    </>
  );
  return <>{canCommit ? <ConfirmationCommitPendingBooking /> : content}</>;
};

export const ConfirmationLayout = memo(Confirmation);
